import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";

interface SlumpTestRecord {
  id?: number;
  testNumber: string;
  batchNumber: string;
  deliveryTicketId: number | null;
  dispatchId: number | null;
  jobId: number | null;

  // Test Details
  testDate: Date;
  testTime: string;
  testLocation: "plant" | "jobsite";
  testPerformedBy: number | null; // User ID

  // Mix Information
  mixDesignId: number | null;
  mixDesignName: string;
  strengthRequirement: string;
  slumpSpecification: number;
  airContentSpecification: number;

  // Test Results
  slumpMeasured: number; // inches
  slumpTolerance: number; // +/- inches
  slumpMeetsSpec: boolean;

  airContentMeasured: number | null; // percentage
  airContentTolerance: number; // +/- percentage
  airContentMeetsSpec: boolean;

  temperatureMeasured: number | null; // Fahrenheit
  temperatureMinSpec: number;
  temperatureMaxSpec: number;
  temperatureMeetsSpec: boolean;

  // Environmental Conditions
  ambientTemperature: number | null;
  weatherConditions: string;
  humidity: number | null;
  windSpeed: number | null;

  // Water Addition
  waterAddedAtPlant: number; // gallons
  waterAddedOnSite: number; // gallons
  totalWaterAdded: number; // gallons
  waterAdditionApproved: boolean;
  approvedBy: string;

  // Acceptance
  testPassed: boolean;
  actionRequired: boolean;
  actionTaken: string;
  notes: string;

  // Documentation
  photosTaken: boolean;
  photoUrls: string[];
  witnessName: string;
  witnessSignature: string;

  // Status
  status: "draft" | "recorded" | "reviewed" | "failed" | "corrected";
}

class SlumpTestRecordingWorkflowStore {
  rootStore: RootStore;

  // Observables
  loading = false;
  error: string | null = null;
  success: string | null = null;
  activeStep = 0;
  testData: SlumpTestRecord;

  // History
  recentTests: any[] = [];

  constructor(rootStore: RootStore) {
    this.testData = this.getDefaultTest();
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  getDefaultTest = (): SlumpTestRecord => ({
    testNumber: this.generateTestNumber(),
    batchNumber: "",
    deliveryTicketId: null,
    dispatchId: null,
    jobId: null,

    testDate: new Date(),
    testTime: new Date().toLocaleTimeString("en-US", { hour12: false }),
    testLocation: "plant",
    testPerformedBy: null,

    mixDesignId: null,
    mixDesignName: "",
    strengthRequirement: "",
    slumpSpecification: 4,
    airContentSpecification: 6,

    slumpMeasured: 0,
    slumpTolerance: 1,
    slumpMeetsSpec: true,

    airContentMeasured: null,
    airContentTolerance: 1.5,
    airContentMeetsSpec: true,

    temperatureMeasured: null,
    temperatureMinSpec: 50,
    temperatureMaxSpec: 90,
    temperatureMeetsSpec: true,

    ambientTemperature: null,
    weatherConditions: "",
    humidity: null,
    windSpeed: null,

    waterAddedAtPlant: 0,
    waterAddedOnSite: 0,
    totalWaterAdded: 0,
    waterAdditionApproved: false,
    approvedBy: "",

    testPassed: true,
    actionRequired: false,
    actionTaken: "",
    notes: "",

    photosTaken: false,
    photoUrls: [],
    witnessName: "",
    witnessSignature: "",

    status: "draft",
  });

  generateTestNumber = (): string => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    const day = now.getDate().toString().padStart(2, "0");
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, "0");
    return `ST${year}${month}${day}-${random}`;
  };

  // Actions
  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  updateTestData = <K extends keyof SlumpTestRecord>(
    field: K,
    value: SlumpTestRecord[K]
  ) => {
    this.testData[field] = value;

    // Auto-calculate total water
    if (field === "waterAddedAtPlant" || field === "waterAddedOnSite") {
      this.testData.totalWaterAdded =
        this.testData.waterAddedAtPlant + this.testData.waterAddedOnSite;
    }

    // Auto-check specifications
    if (field === "slumpMeasured") {
      this.checkSlumpSpec();
    }
    if (field === "airContentMeasured") {
      this.checkAirContentSpec();
    }
    if (field === "temperatureMeasured") {
      this.checkTemperatureSpec();
    }
  };

  checkSlumpSpec = () => {
    const diff = Math.abs(this.testData.slumpMeasured - this.testData.slumpSpecification);
    this.testData.slumpMeetsSpec = diff <= this.testData.slumpTolerance;
    this.updateOverallPassStatus();
  };

  checkAirContentSpec = () => {
    if (this.testData.airContentMeasured === null) {
      this.testData.airContentMeetsSpec = true;
    } else {
      const diff = Math.abs(this.testData.airContentMeasured - this.testData.airContentSpecification);
      this.testData.airContentMeetsSpec = diff <= this.testData.airContentTolerance;
    }
    this.updateOverallPassStatus();
  };

  checkTemperatureSpec = () => {
    if (this.testData.temperatureMeasured === null) {
      this.testData.temperatureMeetsSpec = true;
    } else {
      this.testData.temperatureMeetsSpec =
        this.testData.temperatureMeasured >= this.testData.temperatureMinSpec &&
        this.testData.temperatureMeasured <= this.testData.temperatureMaxSpec;
    }
    this.updateOverallPassStatus();
  };

  updateOverallPassStatus = () => {
    this.testData.testPassed =
      this.testData.slumpMeetsSpec &&
      this.testData.airContentMeetsSpec &&
      this.testData.temperatureMeetsSpec;

    this.testData.actionRequired = !this.testData.testPassed;

    if (!this.testData.testPassed) {
      this.testData.status = "failed";
    }
  };

  addPhoto = (url: string) => {
    this.testData.photoUrls = [...this.testData.photoUrls, url];
    this.testData.photosTaken = this.testData.photoUrls.length > 0;
  };

  removePhoto = (index: number) => {
    this.testData.photoUrls = this.testData.photoUrls.filter((_, i) => i !== index);
    this.testData.photosTaken = this.testData.photoUrls.length > 0;
  };

  // Load dispatch or delivery ticket data
  loadDeliveryData = (deliveryTicketId: number) => {
    // TODO: Load delivery ticket data when service is available
    this.testData.deliveryTicketId = deliveryTicketId;
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.setError(null);

    switch (this.activeStep) {
      case 0: // Test Information
        if (!this.testData.batchNumber) {
          this.setError("Batch number is required");
          return false;
        }
        if (!this.testData.testPerformedBy) {
          this.setError("Please specify who performed the test");
          return false;
        }
        if (!this.testData.mixDesignId) {
          this.setError("Please select a mix design");
          return false;
        }
        break;

      case 1: // Test Results
        if (this.testData.slumpMeasured < 0 || this.testData.slumpMeasured > 12) {
          this.setError("Slump measurement must be between 0 and 12 inches");
          return false;
        }
        if (this.testData.airContentMeasured !== null) {
          if (this.testData.airContentMeasured < 0 || this.testData.airContentMeasured > 20) {
            this.setError("Air content must be between 0 and 20%");
            return false;
          }
        }
        if (this.testData.temperatureMeasured !== null) {
          if (this.testData.temperatureMeasured < 30 || this.testData.temperatureMeasured > 120) {
            this.setError("Temperature must be between 30°F and 120°F");
            return false;
          }
        }
        break;

      case 2: // Water Addition & Action
        if (this.testData.totalWaterAdded > 0 && !this.testData.waterAdditionApproved) {
          this.setError("Water addition must be approved");
          return false;
        }
        if (this.testData.waterAdditionApproved && !this.testData.approvedBy) {
          this.setError("Please specify who approved water addition");
          return false;
        }
        if (this.testData.actionRequired && !this.testData.actionTaken) {
          this.setError("Please specify what action was taken");
          return false;
        }
        break;

      case 3: // Documentation
        if (!this.testData.witnessName) {
          this.setError("Witness name is required");
          return false;
        }
        if (!this.testData.witnessSignature) {
          this.setError("Witness signature is required");
          return false;
        }
        break;
    }

    return true;
  };

  // Async actions
  loadInitialData = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const userStore = this.rootStore.userStore as ServiceStore;
      const mixDesignsStore = this.rootStore.mixDesignsStore as ServiceStore;

      await Promise.all([
        userStore?.find(),
        mixDesignsStore?.find(),
        this.loadRecentTests(),
      ]);
    } catch (err: any) {
      runInAction(() => {
        this.setError("Failed to load initial data");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  loadRecentTests = async () => {
    // TODO: Load recent tests when service is available
    runInAction(() => {
      this.recentTests = [];
    });
  };

  submitTest = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.setLoading(true);
    this.setError(null);

    try {
      // TODO: Create slump test record when backend service is available
      // This would create a slump_tests record

      runInAction(() => {
        this.setSuccess("Slump test recorded successfully!");
        this.testData.status = "recorded";
        this.activeStep = 4; // Success step
      });

    } catch (err: any) {
      runInAction(() => {
        this.setError(err.message || "Failed to record slump test");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  reset = () => {
    this.loading = false;
    this.error = null;
    this.success = null;
    this.activeStep = 0;
    this.testData = this.getDefaultTest();
  };

  // Computed values
  get users() {
    const userStore = this.rootStore.userStore as ServiceStore;
    return userStore?.entities || [];
  }

  get mixDesigns() {
    const mixDesignsStore = this.rootStore.mixDesignsStore as ServiceStore;
    return mixDesignsStore?.entities || [];
  }

  get selectedMixDesign() {
    if (!this.testData.mixDesignId) return null;
    return this.mixDesigns.find((md: any) => md.id === this.testData.mixDesignId);
  }

  get testPerformedByName() {
    if (!this.testData.testPerformedBy) return "";
    const user = this.users.find((u: any) => u.id === this.testData.testPerformedBy);
    return user?.name || "";
  }

  get slumpVariance() {
    return this.testData.slumpMeasured - this.testData.slumpSpecification;
  }

  get slumpVarianceColor() {
    const variance = Math.abs(this.slumpVariance);
    if (variance <= this.testData.slumpTolerance) return "success";
    if (variance <= this.testData.slumpTolerance * 1.5) return "warning";
    return "error";
  }

  get statusColor() {
    const colors: Record<SlumpTestRecord["status"], string> = {
      draft: "#9e9e9e",
      recorded: "#2196f3",
      reviewed: "#4caf50",
      failed: "#f44336",
      corrected: "#ff9800",
    };
    return colors[this.testData.status];
  }
}

export default SlumpTestRecordingWorkflowStore;
